<template>
	<Dialog class="p-fluid"
			style="min-width:600px; width:60vw; max-width:95vw;"
			header="New Signature"
			:modal="true"
			v-model:visible="show"
			@show="onDialogShow">
		<div class="canvas-wrap">
			<canvas ref="canvasRef" class="canvas"></canvas>
		</div>
		<template #footer>
			<div class="flex align-items-center justify-content-end gap-2">
				<Button label="Clear" severity="secondary" text @click="clear" />
				<Button label="Undo" severity="secondary" text @click="undo" />
				<span class="flex-1"></span>
				<Button label="Cancel" severity="secondary" text @click="show = false" />
				<Button label="Update doc" @click="updateSignature(false)" />
				<Button v-if="isInternalUser" label="Save permanently" @click="updateSignature(true)" />
			</div>
		</template>
	</Dialog>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { UpdateSignatureCommand } from '../api-client/api-client';
	import Toast from '@/utils/Toast';
	import SignaturePad from "signature_pad";

	@Options({
		name: 'SignatureModal',
		props: {
			open: { type: Boolean, default: true },
			signatureBase64: { type: String, default: "" }
		},
		emits: ['close', 'update-signature'], // Added missing emit declaration
		watch: {
			open(val: boolean) {
				(this as any).show = val;
			},
			show(val: boolean) {
				if (!val) (this as any).$emit('close');
			}
		}
	})
	export default class SignatureModal extends Vue {
		// Props access for class components
		open!: boolean;
		signatureBase64!: string;

		signaturePad: SignaturePad | null = null;
		strokes: any[] = [];
		show: boolean = false; // Initialize false, let watcher/onMounted handle it
		isLoading: boolean = false;

		created() {
			this.show = this.open;
		}

		async onDialogShow() {
			await this.$nextTick();
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.initPad();
				});
			});
		}

		initPad() {
			const canvas = this.$refs.canvasRef as HTMLCanvasElement | undefined;
			if (!canvas) return;

			const cssWidth = canvas.offsetWidth;
			const cssHeight = canvas.offsetHeight;

			const ratio = Math.max(window.devicePixelRatio || 1, 1);
			canvas.width = Math.floor(cssWidth * ratio);
			canvas.height = Math.floor(cssHeight * ratio);

			const context = canvas.getContext("2d");
			if (context) {
				context.setTransform(ratio, 0, 0, ratio, 0, 0);
			}

			this.signaturePad?.off();
			this.signaturePad = new SignaturePad(canvas, {
				penColor: "#000",
				minWidth: 2.5,
				maxWidth: 5.5,
			});

			if (this.signatureBase64) {
				const dataUrl = this.signatureBase64.startsWith("data:")
					? this.signatureBase64
					: `data:image/png;base64,${this.signatureBase64}`;
				this.signaturePad.fromDataURL(dataUrl);
			}
			// REMOVED: the duplicate this.signaturePad.fromDataURL(dataUrl) that was causing the error
		}

		clear() {
			this.signaturePad?.clear();
			this.strokes = [];
		}

		undo() {
			if (!this.signaturePad) return;
			const data = this.signaturePad.toData();
			data.pop();
			this.signaturePad.fromData(data);
			this.strokes = data;
		}

		get isInternalUser(): boolean {
			return this.$store.getters.isInternalUser;
		}

		async updateSignature(saveToDatabase: boolean) {
			let base64Result = "";

			try {
				this.isLoading = true;
				const canvas = this.$refs.canvasRef as HTMLCanvasElement;
				if (!canvas) return;

				// Using the canvas directly to keep it larger as discussed
				const exportCanvas = (this.signaturePad as any)?.getTrimmedCanvas?.() ?? canvas;

				const dataUrl = exportCanvas.toDataURL("image/png");
				base64Result = dataUrl.split(",")[1] ?? "";

				if (saveToDatabase) {
					const command = new UpdateSignatureCommand();
					command.signatureBase64 = base64Result;
					const apiClient = CompassApi.userApi(this.$store);
					await apiClient.updateSignature(command);
				}

				// Close the modal on success
				this.show = false;
				this.$emit('update-signature', base64Result);

			} catch (error: any) {
				Toast.ApiError("Could not update signature", error);
			} finally {
				this.isLoading = false;
			}
		}
	}
</script>

<style scoped>
	.canvas-wrap {
		border: 2px dashed #bbb;
		border-radius: 8px;
		height: 25rem;
		overflow: hidden;
		background: #fff;
	}

	.canvas {
		width: 100%;
		height: 100%;
		touch-action: none;
		pointer-events: auto;
	}
</style>